import {useLinePlotFlagsStore} from './stores/LinePlotFlagsStore';

/**
 * Public readers for line-plot ramp flags. The real evaluation lives in
 * `./stores/LinePlotFlagsStore` (hydrated once per route via
 * `useInitializeLinePlotFlagsStore`); these hooks are thin accessors over
 * that precomputed snapshot so panels don't re-run the combinators, the
 * Datadog RUM tagging, or the `envIsServerDeployment`/`envIsIntegration` branches on
 * every render.
 *
 * Callers that previously passed `entityName` no longer need to - the
 * entity flag store is hydrated from the current route's `useProjectParams`
 * so the store knows which entity is active.
 */

export const useRampFlagUseBackendSmoothing = () => {
  return useLinePlotFlagsStore(state => state.isBackendSmoothingEnabled);
};

export const useEnableBackendGrouping = () => {
  return useLinePlotFlagsStore(state => state.isBackendGroupingEnabled);
};

export const useRampFlagLineConstructionProfiling = () => {
  return useLinePlotFlagsStore(
    state => state.isLineConstructionProfilingEnabled
  );
};

export const useRampFlagFullFidelityRanges = () => {
  return useLinePlotFlagsStore(state => state.isFullFidelityRangesEnabled);
};

/**
 * This currently doesn't support an entity flag because I didn't want to do the work to thread and entity value into the check. It probably won't be that hard but we're just experimenting currently.
 */
export const useRampFlagFullFidelityRunsPerChart = () => {
  return useLinePlotFlagsStore(
    state => state.isFullFidelityRunsPerChartEnabled
  );
};

/**
 * User-level feature flag for higher full-screen line plot max runs
 * (backend: increase_full_screen_max_runs_user).
 */
export const useRampFlagIncreaseFullScreenMaxRuns = () => {
  return useLinePlotFlagsStore(
    state => state.isIncreaseFullScreenMaxRunsEnabled
  );
};

/**
 * Org-level flag for dynamic full-screen bin sizing.
 * When enabled, the full-screen 10k query uses Math.min(width * 4, 10000)
 * instead of a hardcoded 10,000 bins, matching uPlot's decimation threshold.
 */
export const useRampFlagDynamicFullScreenBinSizing = () => {
  return useLinePlotFlagsStore(
    state => state.isDynamicFullScreenBinSizingEnabled
  );
};

export const useStatsigGateBackendLineConstruction = () => {
  return useLinePlotFlagsStore(state => state.isBackendLineConstructionEnabled);
};

export const useStatsigGateBackendLineConstructionGrouped = () => {
  return useLinePlotFlagsStore(
    state => state.isBackendLineConstructionGroupedEnabled
  );
};

export const useStatsigGateBackendLineConstructionSmoothing = () => {
  return useLinePlotFlagsStore(
    state => state.isBackendLineConstructionSmoothingEnabled
  );
};

export const useStatsigGateBackendLineConstructionReports = () => {
  return useLinePlotFlagsStore(
    state => state.isBackendLineConstructionReportsEnabled
  );
};

export const useStatsigGateColumnarBucketed = () => {
  return useLinePlotFlagsStore(state => state.isColumnarBucketedEnabled);
};
